/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Avatar, Rate, Tag, Typography } from 'antd';
import { LikeOutlined, DislikeOutlined, UserOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import type {
  BinaryReview,
  StarReview,
  ReviewPhase,
  ReviewServiceFunctions,
} from './types';
import { CommentSection } from './CommentSection';
import { getInitials } from './utils';

type ReviewCardProps = {
  review: BinaryReview | StarReview;
  phase: ReviewPhase;
  isCurrentUser?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  enableComments?: boolean;
  showComments?: boolean;
  reviewService?: ReviewServiceFunctions;
  contextIds?: Record<string, number | string>;
  entityId?: number;
  onCommentsChange?: () => void;
  isReadOnly?: boolean;
};

export const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  phase,
  isCurrentUser = false,
  onEdit,
  onDelete,
  enableComments = false,
  showComments = false,
  reviewService,
  contextIds,
  entityId,
  onCommentsChange,
  isReadOnly = false,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const isBinary = phase === 'screening';
  const binaryRating = isBinary ? (review as BinaryReview).binary_rating : null;
  const starRating = !isBinary ? (review as StarReview).star_rating : null;

  const summaryTags = review.summary
    ? review.summary
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    : [];

  return (
    <div css={styles.card}>
      <div css={styles.header}>
        <div css={styles.authorSection}>
          <Avatar css={styles.avatar} icon={<UserOutlined rev={''} />}>
            {getInitials(review.created_user_full_name)}
          </Avatar>

          <div css={styles.authorInfo}>
            <Typography.Text strong css={styles.authorName}>
              {review.created_user_full_name ||
                intl.formatMessage({ id: 'review-unknown-user' })}
            </Typography.Text>
            <Typography.Text type="secondary" css={styles.timestamp}>
              {moment.unix(review.created_at).fromNow()}
            </Typography.Text>
          </div>
        </div>
      </div>

      <div css={styles.ratingSection}>
        {isBinary && binaryRating !== null && (
          <div css={styles.binaryRating}>
            {binaryRating ? (
              <Tag
                color="success"
                icon={<LikeOutlined rev={''} />}
                css={styles.binaryTag}>
                {intl.formatMessage({ id: 'review-thumbs-up' })}
              </Tag>
            ) : (
              <Tag
                color="error"
                icon={<DislikeOutlined rev={''} />}
                css={styles.binaryTag}>
                {intl.formatMessage({ id: 'review-thumbs-down' })}
              </Tag>
            )}
          </div>
        )}

        {!isBinary && starRating !== null && (
          <Rate disabled value={starRating} css={styles.starRating} />
        )}
      </div>

      <Typography.Title level={5} css={styles.title}>
        {review.title}
      </Typography.Title>

      {summaryTags.length > 0 && (
        <div css={styles.summaryTags}>
          {summaryTags.map((tag, index) => (
            <Tag key={index} color="blue" css={styles.summaryTag}>
              {tag}
            </Tag>
          ))}
        </div>
      )}

      {review.content && (
        <Typography.Paragraph css={styles.content}>
          {review.content}
        </Typography.Paragraph>
      )}

      {enableComments && reviewService && contextIds && entityId && (
        <CommentSection
          reviewId={review.id}
          entityId={entityId}
          contextIds={contextIds}
          reviewService={reviewService}
          comments={review.comments || []}
          onCommentsChange={onCommentsChange}
          showComments={showComments}
          isReadOnly={isReadOnly}
        />
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  card: {
    background: '#ffffff',
    border: '1px solid #f0f0f0',
    borderRadius: 10,
    padding: '16px 20px',
    marginBottom: 12,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'box-shadow 0.2s ease',

    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
  },

  header: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 12,
  },

  authorSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    flex: 1,
  },

  avatar: {
    background: '#e6f7ff',
    color: '#1890ff',
    flexShrink: 0,
  },

  authorInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },

  authorName: {
    fontSize: 14,
  },

  timestamp: {
    fontSize: 12,
  },

  ratingSection: {
    marginBottom: 12,
  },

  binaryRating: {
    display: 'flex',
    alignItems: 'center',
  },

  binaryTag: {
    fontSize: 13,
    padding: '4px 10px',
    margin: 0,
  },

  starRating: {
    fontSize: 18,
  },

  title: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    marginTop: 0,
  },

  summaryTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },

  summaryTag: {
    margin: 0,
    fontSize: 12,
  },

  content: {
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
});
